import React from "react";
import { Dialog, DialogContent, DialogActions, Button, Divider, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import AppTextField from "../../../lib/components/AppTextField";
import walletService from "../../../service/walletService";
import AppDateField from "../../../lib/components/AppDateField";
import { toast } from "react-toastify";
import BTNLoader from "../../../lib/components/BTNLoader";
import moment from "moment";

const FundWalletModal = ({ open, onClose, refetch }) => {
  const [amount, setAmount] = React.useState("");
  const [date, setDate] = React.useState(new Date());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setAmount("");
      setDate(new Date());
      setErrorText("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");

    const numAmount = Number(amount);
    if (!amount || numAmount <= 0) {
      setErrorText("Please enter a valid funding amount");
      return;
    }

    if (!date) {
      setErrorText("Please select a funding date");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        amount: numAmount,
        date: moment(date).format("YYYY-MM-DD"),
        source: "paystack",
      };
      await walletService.logFunding(payload);
      toast.success("Funding logged successfully!");
      if (refetch) refetch();
      onClose();
    } catch (err) {
      let errMsg = "Failed to log wallet funding";
      const backendMessage = err?.response?.data?.message;
      if (backendMessage) {
        if (typeof backendMessage === "object") {
          errMsg = Object.values(backendMessage).join(", ");
        } else {
          errMsg = backendMessage;
        }
      } else if (err?.message) {
        errMsg = err.message;
      }
      setErrorText(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <div className="d-flex justify-content-between gap-4 p-3 py-2 pe-2">
        <div>
          <div className="fs-5 fw-medium">Fund Wallet</div>
          <div className="fs-sm text-secondary">
            Enter your required funding amount to record a manual wallet replenishment log.
          </div>
        </div>
        <div>
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <Close />
          </IconButton>
        </div>
      </div>
      <Divider className="bg-secondary-subtle" />
      <DialogContent>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="d-block">
              Date <span className="text-danger">*</span>
            </label>
            <AppDateField
              value={date}
              onChange={(newVal) => setDate(newVal)}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="mb-4">
            <label className="d-block">
              Amount <span className="text-danger">*</span>
            </label>
            <AppTextField
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              disabled={isSubmitting}
              required
            />
          </div>

          {errorText && (
            <div className="text-danger fs-sm mb-3">{errorText}</div>
          )}

          <DialogActions className="px-0">
            <Button onClick={onClose} disabled={isSubmitting} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !amount || !date}
              variant="contained"
              color="secondary"
              startIcon={<BTNLoader isLoading={isSubmitting} />}
            >
              Confirm Funding
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FundWalletModal;
